/**
 * Background Processing Module
 *
 * Handles async OCR processing, server-side queue delegation,
 * and client-side fallback processing for scanned book pages.
 * Extracted from scannedBookService.ts (v8.74).
 *
 * Responsibilities:
 * - Trigger and manage background OCR processing
 * - Server-side queue delegation with client fallback
 * - Page-level OCR with retry and exponential backoff
 * - Processing lock management (prevent double-triggers)
 * - Processing status tracking
 */

import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import { runClientSideConsolidation } from './consolidation';
import { generateAllDigitalPagesV9 } from '../scannedBookService';

// ============================================================================
// Types
// ============================================================================

export interface CaptureForStorage {
  pageNumber: number;
  rawImage: string; // base64
  bookTitle?: string;
  captureQuality?: 'excellent' | 'good' | 'fair' | 'poor';
}

export interface SaveCapturesResult {
  savedPages: number[];
  errors: Array<{ pageNumber: number; error: string }>;
  bookId: string;
}

interface ServerQueueResult {
  success: boolean;
  totalPages?: number;
  processed?: number;
  errors?: number;
  duration?: number;
  error?: string;
}

// ============================================================================
// Private Helpers
// ============================================================================

/**
 * Get the Supabase Edge Functions base URL
 */
function getEdgeFunctionUrl(functionName: string): string {
  const projectRef = import.meta.env.VITE_SUPABASE_URL?.match(/https:\/\/([^.]+)/)?.[1] || 'eriveqrkhuxgrnoqnamd';
  return `https://${projectRef}.supabase.co/functions/v1/${functionName}`;
}

/**
 * Call an edge function using direct fetch (more reliable than supabase.functions.invoke)
 */
async function callEdgeFunction(
  functionName: string,
  body: object,
  timeoutMs: number = 90000
): Promise<{ data: any; error: Error | null }> {
  const url = getEdgeFunctionUrl(functionName);
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      return { data: null, error: new Error(`Edge Function returned ${response.status}: ${errorText.slice(0, 200)}`) };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return { data: null, error: new Error(`Edge Function timeout after ${timeoutMs}ms`) };
    }
    return { data: null, error: err instanceof Error ? err : new Error('Unknown fetch error') };
  }
}

/**
 * Warm up the OCR edge function to avoid cold start timeouts
 */
async function warmUpOCRFunction(): Promise<boolean> {
  try {
    const tinyImage = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

    console.log('[WarmUp] Warming up google-vision-ocr...');
    const startTime = Date.now();

    const { data, error } = await callEdgeFunction('google-vision-ocr', { image: tinyImage });

    const elapsed = Date.now() - startTime;

    if (error) {
      console.warn(`[WarmUp] Warm-up call failed (${elapsed}ms):`, error.message);
      return false;
    }

    console.log(`[WarmUp] Function ready (${elapsed}ms)`);
    return true;
  } catch (err) {
    console.warn('[WarmUp] Warm-up failed:', err);
    return false;
  }
}

// Processing lock to prevent double-triggering
const processingLocks = new Set<string>();

/**
 * Trigger server-side queue processing
 */
async function triggerServerSideProcessing(bookId: string): Promise<ServerQueueResult> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  console.log(`[ServerQueue] Triggering server-side processing for book ${bookId}`);

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/process-book-queue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({ book_id: bookId }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.error(`[ServerQueue] HTTP error: ${response.status} - ${errorText}`);
      Sentry.captureException(new Error(`ServerQueue HTTP ${response.status}: ${errorText.slice(0, 100)}`), {
        tags: { flow: 'scan_pipeline', step: 'server_queue' },
      });
      return {
        success: false,
        error: `Server returned ${response.status}: ${errorText.slice(0, 100)}`
      };
    }

    const result = await response.json();

    if (!result.success) {
      console.error(`[ServerQueue] Processing failed:`, result.error);
      Sentry.captureException(new Error(String(result.error)), {
        tags: { flow: 'scan_pipeline', step: 'server_queue' },
      });
      return { success: false, error: result.error };
    }

    console.log(`[ServerQueue] Success: ${result.processed}/${result.totalPages} pages in ${result.duration}ms`);

    return {
      success: true,
      totalPages: result.totalPages,
      processed: result.processed,
      errors: result.errors,
      duration: result.duration,
    };

  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[ServerQueue] Network error:`, error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'server_queue' },
    });
    return { success: false, error: message };
  }
}

/**
 * Process a single page with retry logic for cold start issues
 */
async function processPageOCRWithRetry(
  bookId: string,
  pageNumber: number,
  imageUrl: string | null,
  bookTitle: string,
  previousPageText?: string,
  maxRetries: number = 3
): Promise<{ success: boolean; error?: string }> {
  let lastError = '';

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const result = await processPageOCRBackground(bookId, pageNumber, imageUrl, bookTitle, previousPageText);

    if (result.success) {
      return result;
    }

    lastError = result.error || 'Unknown error';

    const isRetryable = lastError.includes('non-2xx') ||
                        lastError.includes('503') ||
                        lastError.includes('429') ||
                        lastError.includes('timeout') ||
                        lastError.includes('rate');

    if (isRetryable) {
      const baseDelay = lastError.includes('429') || lastError.includes('rate') ? 10000 : 2000;
      const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), 30000);
      console.log(`[ProcessPageOCR] Page ${pageNumber} attempt ${attempt} failed (${lastError.slice(0, 50)}), retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    } else {
      break;
    }
  }

  return { success: false, error: lastError };
}

/**
 * Process a single page via Google Cloud Vision OCR (primary, lightweight)
 */
async function processPageOCRBackground(
  bookId: string,
  pageNumber: number,
  imageUrl: string | null,
  bookTitle: string,
  previousPageText?: string
): Promise<{ success: boolean; error?: string }> {
  if (!imageUrl) {
    return { success: false, error: 'No image URL' };
  }

  try {
    await supabase
      .from('scanned_book_pages')
      .update({ processing_status: 'processing' })
      .eq('book_id', bookId)
      .eq('page_number', pageNumber);

    console.log(`[v11] Processing page ${pageNumber} with dual extraction`);

    const { data: result, error: extractError } = await callEdgeFunction('extract-page-dual', {
      image_url: imageUrl,
      page_number: pageNumber
    });

    if (extractError || !result?.success) {
      console.warn(`[v11] Dual extraction failed, falling back to legacy extraction`);
      return processPageOCRBackgroundLegacy(bookId, pageNumber, imageUrl, bookTitle, previousPageText);
    }

    const sentences = result.sentences || [];
    const extractedText = sentences.join('\n');
    const confidence = result.confidence || 'medium';

    let pageType = 'story';
    if (sentences.length === 0) {
      pageType = 'illustration_only';
    } else if (result.gcv?.fullText?.toLowerCase().includes('copyright') ||
               result.gcv?.fullText?.toLowerCase().includes('isbn')) {
      pageType = 'front_matter';
    }

    const qualityIssues: string[] = [];
    if (result.validation?.hallucinationCount > 0) {
      qualityIssues.push(`Removed ${result.validation.hallucinationCount} hallucinations`);
    }
    if (result.validation?.omissions?.marginBleed?.length > 0) {
      qualityIssues.push(`Filtered ${result.validation.omissions.marginBleed.length} margin bleed words`);
    }
    if (confidence === 'low') {
      qualityIssues.push('Low confidence - may need review');
    }

    console.log(`[v11] Page ${pageNumber}: ${sentences.length} sentences, confidence=${confidence}`);

    const { error: updateError } = await supabase
      .from('scanned_book_pages')
      .update({
        extracted_text: extractedText,
        extraction_confidence: confidence === 'high' ? 0.95 : confidence === 'medium' ? 0.80 : 0.60,
        page_type: pageType,
        has_illustration: true,
        illustration_description: null,
        layout_data: null,
        rendering_hints: { textSize: 'large', textAlignment: 'left' },
        text_blocks: sentences.length > 0 ? sentences.map((s: string) => ({ type: 'sentence', text: s })) : null,
        detected_page_number: null,
        quality_issues: qualityIssues.length > 0 ? qualityIssues : null,
        processing_status: 'complete',
        verification_data: {
          source: 'dual-extraction-v11',
          confidence,
          validation: result.validation,
          gcvWordCount: result.gcv?.wordCount
        },
        sentences: sentences,
        raw_ocr_text: result.gcv?.fullText || null,
      })
      .eq('book_id', bookId)
      .eq('page_number', pageNumber);

    if (updateError) {
      throw new Error(`DB update failed: ${updateError.message}`);
    }

    console.log(`[ProcessPageOCR] Page ${pageNumber} complete (${result.wordCount || 0} words, type: ${pageType})`);
    return { success: true };

  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error(`[ProcessPageOCR] Page ${pageNumber} failed:`, message);
    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
      tags: { flow: 'scan_pipeline', step: 'process_page_ocr' },
    });

    await supabase
      .from('scanned_book_pages')
      .update({
        processing_status: 'error',
        quality_issues: [`OCR failed: ${message}`],
      })
      .eq('book_id', bookId)
      .eq('page_number', pageNumber);

    return { success: false, error: message };
  }
}

/**
 * Legacy fallback for processPageOCRBackground
 */
async function processPageOCRBackgroundLegacy(
  bookId: string,
  pageNumber: number,
  imageUrl: string,
  bookTitle: string,
  previousPageText?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const imageResponse = await fetch(imageUrl);
    if (!imageResponse.ok) {
      throw new Error(`Failed to fetch image: ${imageResponse.status}`);
    }

    const imageBlob = await imageResponse.blob();
    const base64Image = await blobToBase64(imageBlob);

    const { data: result, error: extractError } = await callEdgeFunction('extract-page-content', {
      image: base64Image,
      context: {
        bookTitle,
        previousPageText: previousPageText?.slice(-300) || null,
        pageNumber
      }
    });

    if (extractError || !result) {
      const { data: ocrResult, error: ocrError } = await callEdgeFunction('google-vision-ocr', { image: base64Image });

      if (ocrError || !ocrResult) {
        throw ocrError || new Error('All extraction methods failed');
      }

      const extractedText = ocrResult.text || '';
      const pageType = inferPageType(extractedText, ocrResult.wordCount || 0);

      await supabase
        .from('scanned_book_pages')
        .update({
          extracted_text: extractedText,
          extraction_confidence: ocrResult.confidence ?? null,
          page_type: pageType,
          processing_status: 'complete',
          quality_issues: ['Raw OCR fallback'],
        })
        .eq('book_id', bookId)
        .eq('page_number', pageNumber);

      return { success: true };
    }

    const extractedText = result.text || '';
    const pageType = result.classification?.pageType || result.pageType || 'text';

    await supabase
      .from('scanned_book_pages')
      .update({
        extracted_text: extractedText,
        extraction_confidence: result.confidence ?? null,
        page_type: pageType,
        processing_status: 'complete',
        verification_data: result.classification || null,
        raw_ocr_text: result.rawOcrText || null,
      })
      .eq('book_id', bookId)
      .eq('page_number', pageNumber);

    console.log(`[Legacy] Page ${pageNumber} complete via legacy extraction`);
    return { success: true };

  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error(`[Legacy] Page ${pageNumber} failed:`, message);
    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
      tags: { flow: 'scan_pipeline', step: 'legacy_page_ocr' },
    });

    await supabase
      .from('scanned_book_pages')
      .update({
        processing_status: 'error',
        quality_issues: [`Legacy extraction failed: ${message}`],
      })
      .eq('book_id', bookId)
      .eq('page_number', pageNumber);

    return { success: false, error: message };
  }
}

/**
 * Infer page type from text content (simple heuristic)
 */
function inferPageType(text: string, wordCount: number): string {
  const lowerText = text.toLowerCase();

  if (wordCount < 10) {
    if (/chapter\s+\d|part\s+\d/i.test(text)) return 'chapter';
    if (/copyright|published|isbn/i.test(lowerText)) return 'copyright';
    if (/dedicated|for\s+my/i.test(lowerText)) return 'dedication';
    return 'illustration';
  }

  if (/contents|table of contents/i.test(lowerText) || /\.\.\.\s*\d+/g.test(text)) return 'toc';
  if (/copyright|all rights reserved|published by|isbn/i.test(lowerText)) return 'copyright';
  if (wordCount < 30 && /by\s+[A-Z]/i.test(text)) return 'title';
  if (/^chapter\s+\d|^part\s+\d/im.test(text)) return 'chapter';

  return 'text';
}

/**
 * Convert blob to base64 string
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ============================================================================
// Exported Functions
// ============================================================================

/**
 * Save captures to storage and create placeholder pages for background OCR.
 */
export async function saveCapturesToStorage(
  bookId: string,
  captures: CaptureForStorage[]
): Promise<SaveCapturesResult> {
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id || 'anonymous';

  const savedPages: number[] = [];
  const errors: Array<{ pageNumber: number; error: string }> = [];

  console.log(`[SaveCaptures] Saving ${captures.length} captures for book ${bookId}`);

  const BATCH_SIZE = 3;

  for (let i = 0; i < captures.length; i += BATCH_SIZE) {
    const batch = captures.slice(i, i + BATCH_SIZE);

    await Promise.all(batch.map(async (capture) => {
      const { pageNumber, rawImage, captureQuality } = capture;

      try {
        let imageUrl: string | null = null;

        const base64Data = rawImage.replace(/^data:image\/\w+;base64,/, '');
        const imageBuffer = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
        const imagePath = `${userId}/${bookId}/page-${pageNumber}-raw.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('scanned-pages')
          .upload(imagePath, imageBuffer, {
            contentType: 'image/jpeg',
            upsert: true,
          });

        if (uploadError) {
          errors.push({ pageNumber, error: `Upload failed: ${uploadError.message}` });
          return;
        }

        const { data: urlData } = supabase.storage
          .from('scanned-pages')
          .getPublicUrl(imagePath);
        imageUrl = urlData?.publicUrl || null;

        const { error: dbError } = await supabase
          .from('scanned_book_pages')
          .upsert(
            {
              book_id: bookId,
              page_number: pageNumber,
              image_url: imageUrl,
              extracted_text: '',
              extraction_confidence: null,
              page_type: 'text',
              processing_status: 'pending',
              capture_quality: captureQuality || null,
              has_illustration: false,
              needs_rescan: false,
            } as any,
            { onConflict: 'book_id,page_number' }
          );

        if (dbError) {
          errors.push({ pageNumber, error: `DB insert failed: ${dbError.message}` });
          return;
        }

        savedPages.push(pageNumber);
        console.log(`[SaveCaptures] Page ${pageNumber}: Saved successfully`);

      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        errors.push({ pageNumber, error: message });
      }
    }));
  }

  console.log(`[SaveCaptures] Complete: ${savedPages.length} saved, ${errors.length} errors`);

  return { savedPages, errors, bookId };
}

/**
 * Trigger background processing for a book.
 *
 * Processes pages via server-side queue with client-side fallback.
 * Includes lock to prevent double-triggering.
 */
export async function triggerBackgroundProcessing(bookId: string): Promise<{ success: boolean; error?: string }> {
  if (processingLocks.has(bookId)) {
    console.warn(`[BackgroundProcessing] Book ${bookId} already processing, ignoring duplicate trigger`);
    return { success: true, error: 'Already processing' };
  }
  processingLocks.add(bookId);

  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      processingLocks.delete(bookId);
      return { success: false, error: 'Not authenticated' };
    }

    // Reset stuck pages
    const { data: stuckPages } = await supabase
      .from('scanned_book_pages')
      .select('page_number')
      .eq('book_id', bookId)
      .eq('processing_status', 'processing');

    if (stuckPages && stuckPages.length > 0) {
      console.warn(`[BackgroundProcessing] Found ${stuckPages.length} stuck pages in "processing", resetting to pending`);
      await supabase
        .from('scanned_book_pages')
        .update({ processing_status: 'pending' } as any)
        .eq('book_id', bookId)
        .eq('processing_status', 'processing');
    }

    const { data: pendingPages, error: fetchError } = await supabase
      .from('scanned_book_pages')
      .select('page_number')
      .eq('book_id', bookId)
      .eq('processing_status', 'pending');

    if (fetchError) {
      console.error(`[BackgroundProcessing] Failed to fetch pages:`, fetchError);
      Sentry.captureException(fetchError instanceof Error ? fetchError : new Error(String(fetchError)), {
        tags: { flow: 'scan_pipeline', step: 'background_processing' },
      });
      processingLocks.delete(bookId);
      return { success: false, error: fetchError.message };
    }

    if (!pendingPages || pendingPages.length === 0) {
      console.log(`[BackgroundProcessing] No pending pages for book ${bookId}`);
      await runClientSideConsolidation(bookId);
      await generateAllDigitalPagesV9(bookId);
      processingLocks.delete(bookId);
      return { success: true };
    }

    console.log(`[BackgroundProcessing] ${pendingPages.length} pending pages - using server-side queue`);

    const serverQueueResult = await triggerServerSideProcessing(bookId);

    if (serverQueueResult.success) {
      console.log(`[BackgroundProcessing] Server queue complete: ${serverQueueResult.processed}/${serverQueueResult.totalPages} pages`);
      await runClientSideConsolidation(bookId);
      await generateAllDigitalPagesV9(bookId);
      processingLocks.delete(bookId);
      return { success: true };
    }

    // Fallback to client-side processing
    console.warn(`[BackgroundProcessing] Server queue failed, falling back to client-side processing`);

    const { data: book } = await supabase
      .from('scanned_books')
      .select('title')
      .eq('id', bookId)
      .maybeSingle();

    const bookTitle = book?.title || 'Unknown Book';

    const { data: pendingPagesWithUrls } = await supabase
      .from('scanned_book_pages')
      .select('page_number, image_url')
      .eq('book_id', bookId)
      .eq('processing_status', 'pending')
      .order('page_number');

    if (!pendingPagesWithUrls || pendingPagesWithUrls.length === 0) {
      processingLocks.delete(bookId);
      return { success: true };
    }

    const pendingPagesForFallback = pendingPagesWithUrls;

    console.log(`[BackgroundProcessing] Client-side fallback: processing ${pendingPagesForFallback.length} pages`);

    const { data: processedPages } = await supabase
      .from('scanned_book_pages')
      .select('page_number, extracted_text')
      .eq('book_id', bookId)
      .eq('processing_status', 'complete')
      .order('page_number');

    const pageTextMap = new Map<number, string>();
    if (processedPages) {
      for (const p of processedPages) {
        pageTextMap.set(p.page_number, p.extracted_text || '');
      }
    }

    await warmUpOCRFunction();

    let processed = 0;
    let errors = 0;

    const BATCH_SIZE = 5;
    const BATCH_DELAY_MS = 1500;

    const batches: typeof pendingPagesForFallback[] = [];
    for (let i = 0; i < pendingPagesForFallback.length; i += BATCH_SIZE) {
      batches.push(pendingPagesForFallback.slice(i, i + BATCH_SIZE));
    }

    console.log(`[BackgroundProcessing] Processing ${pendingPagesForFallback.length} pages in ${batches.length} batches of ${BATCH_SIZE}`);

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];

      const batchPromises = batch.map(async (page) => {
        const result = await processPageOCRWithRetry(
          bookId,
          page.page_number,
          page.image_url,
          bookTitle,
          undefined
        );
        return { pageNumber: page.page_number, ...result };
      });

      const batchResults = await Promise.all(batchPromises);

      for (const result of batchResults) {
        if (result.success) {
          processed++;
        } else {
          errors++;
          console.warn(`[BackgroundProcessing] Page ${result.pageNumber} failed: ${result.error}`);
        }
      }

      console.log(`[BackgroundProcessing] Batch ${batchIndex + 1}/${batches.length} complete: ${processed} processed, ${errors} errors`);

      if (batchIndex < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
      }
    }

    console.log(`[BackgroundProcessing] Complete: ${processed}/${pendingPages.length} pages, ${errors} errors`);

    await runClientSideConsolidation(bookId);

    try {
      console.log('[BackgroundProcessing] Using v9 book-level reconstruction...');
      const digitalPageCount = await generateAllDigitalPagesV9(bookId);
      console.log(`[BackgroundProcessing] Generated ${digitalPageCount} digital pages (v9)`);
    } catch (dpError) {
      console.error('[BackgroundProcessing] Digital page generation failed:', dpError);
      Sentry.captureException(dpError instanceof Error ? dpError : new Error(String(dpError)), {
        tags: { flow: 'scan_pipeline', step: 'background_processing' },
      });
    }

    processingLocks.delete(bookId);
    return { success: true };

  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error(`[BackgroundProcessing] Error:`, err);
    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
      tags: { flow: 'scan_pipeline', step: 'background_processing' },
    });
    processingLocks.delete(bookId);
    return { success: false, error: message };
  }
}

/**
 * Get pages that need rescan.
 * Returns page numbers with processing_status = 'error', needs_rescan = true,
 * or very low extraction confidence (<30%).
 */
export async function getPagesNeedingRescan(bookId: string): Promise<number[]> {
  const { data: pages, error } = await supabase
    .from('scanned_book_pages')
    .select('page_number, processing_status, needs_rescan, extraction_confidence')
    .eq('book_id', bookId)
    .order('page_number');

  if (error || !pages) {
    console.warn(`[Rescan] Failed to check rescan needs for book ${bookId}:`, error?.message);
    return [];
  }

  const needsRescan: number[] = [];

  for (const page of pages) {
    const p = page as any;
    const isError = p.processing_status === 'error';
    const flaggedForRescan = p.needs_rescan === true;
    const veryLowConfidence = (p.extraction_confidence || 100) < 30;

    if (isError || flaggedForRescan || veryLowConfidence) {
      needsRescan.push(p.page_number);
    }
  }

  if (needsRescan.length > 0) {
    console.log(`[Rescan] Book ${bookId} has ${needsRescan.length} pages needing rescan: ${needsRescan.join(', ')}`);
  }

  return needsRescan;
}

/**
 * Get processing status for a book.
 */
export async function getBookProcessingStatus(bookId: string): Promise<{
  totalPages: number;
  pendingPages: number;
  processingPages: number;
  completePages: number;
  errorPages: number;
  isComplete: boolean;
}> {
  const { data: pages, error } = await supabase
    .from('scanned_book_pages')
    .select('processing_status')
    .eq('book_id', bookId);

  if (error || !pages) {
    return {
      totalPages: 0,
      pendingPages: 0,
      processingPages: 0,
      completePages: 0,
      errorPages: 0,
      isComplete: true,
    };
  }

  const counts = { pending: 0, processing: 0, complete: 0, error: 0 };

  for (const page of pages) {
    const status = (page as any).processing_status || 'complete';
    if (status in counts) {
      counts[status as keyof typeof counts]++;
    }
  }

  return {
    totalPages: pages.length,
    pendingPages: counts.pending,
    processingPages: counts.processing,
    completePages: counts.complete,
    errorPages: counts.error,
    isComplete: counts.pending === 0 && counts.processing === 0,
  };
}
